import {
  ArrowCounterClockwise,
  Basket,
  BowlFood,
  CaretRight,
  Check,
  CookingPot,
  CreditCard,
  DotsThree,
  ForkKnife,
  House,
  MapPin,
  Minus,
  Motorcycle,
  Plus,
  QrCode,
  Receipt,
  ShoppingCart,
  Storefront,
  Trash,
  Truck,
  Warning,
  X
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { addresses, categories, menuItems, statusLabels } from "./data";
import {
  formatMoney,
  getCartAllergens,
  getDeliveryFee,
  getItem,
  getLineUnitPrice,
  getSubtotal,
  validateRequiredModifiers
} from "./logic";
import type { Address, AppTab, CartLine, DemoOrder, EntryMode, MenuItem } from "./types";

type PaymentMethod = "zalopay" | "cod";

export function App() {
  const [mode, setMode] = useState<EntryMode | null>(null);
  const [choosingAddress, setChoosingAddress] = useState(false);
  const [addressId, setAddressId] = useState("");
  const [addressError, setAddressError] = useState("");
  const [tab, setTab] = useState<AppTab>("menu");
  const [category, setCategory] = useState(categories[0]);
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [modifiers, setModifiers] = useState<Record<string, string>>({});
  const [itemNote, setItemNote] = useState("");
  const [itemError, setItemError] = useState("");
  const [cart, setCart] = useState<CartLine[]>([]);
  const [nextLineNumber, setNextLineNumber] = useState(1);
  const [lastRemoved, setLastRemoved] = useState<CartLine | null>(null);
  const [allergenAccepted, setAllergenAccepted] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("zalopay");
  const [checkoutError, setCheckoutError] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [order, setOrder] = useState<DemoOrder | null>(null);
  const [additionalRound, setAdditionalRound] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [switchConfirm, setSwitchConfirm] = useState(false);
  const [toast, setToast] = useState("");

  const openItem = menuItems.find((item) => item.id === openItemId);
  const selectedAddress = addresses.find((address) => address.id === addressId);
  const subtotal = useMemo(() => getSubtotal(cart), [cart]);
  const deliveryFee = mode === "delivery" ? getDeliveryFee(addressId) : 0;
  const total = subtotal + Math.max(0, deliveryFee);
  const cartCount = cart.reduce((sum, line) => sum + line.quantity, 0);
  const allergens = useMemo(() => getCartAllergens(cart), [cart]);
  const visibleItems = menuItems.filter((item) => item.category === category);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!openItemId && !switchConfirm) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpenItemId(null);
      setSwitchConfirm(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openItemId, switchConfirm]);

  const enterTable = () => {
    setMode("table");
    setChoosingAddress(false);
    setAddressId("");
    setTab("menu");
  };

  const startDelivery = () => {
    setChoosingAddress(true);
    setAddressError("");
  };

  const chooseAddress = (address: Address) => {
    setAddressError("");
    if (getDeliveryFee(address.id) < 0) {
      setAddressError("Địa chỉ mẫu này cách quán 6,4km, ngoài vùng giao 5km.");
      return;
    }
    setAddressId(address.id);
    setMode("delivery");
    setChoosingAddress(false);
    setTab("menu");
  };

  const openItemSheet = (item: MenuItem) => {
    if (!item.available) return;
    setOpenItemId(item.id);
    setModifiers({});
    setItemNote("");
    setItemError("");
  };

  const addItem = () => {
    if (!openItem) return;
    const validation = validateRequiredModifiers(openItem.id, modifiers);
    if (validation) {
      setItemError(validation);
      return;
    }
    const line: CartLine = {
      id: `line-${nextLineNumber}`,
      itemId: openItem.id,
      quantity: 1,
      modifiers: { ...modifiers },
      note: itemNote.trim()
    };
    setNextLineNumber((value) => value + 1);
    setCart((current) => [...current, line]);
    setOpenItemId(null);
    setAllergenAccepted(false);
    setToast(`Đã thêm ${openItem.name} vào giỏ.`);
  };

  const updateQuantity = (lineId: string, delta: number) => {
    setCart((current) =>
      current
        .map((line) => (line.id === lineId ? { ...line, quantity: Math.max(0, line.quantity + delta) } : line))
        .filter((line) => line.quantity > 0)
    );
    setAllergenAccepted(false);
  };

  const removeLine = (line: CartLine) => {
    setCart((current) => current.filter((candidate) => candidate.id !== line.id));
    setLastRemoved(line);
    setAllergenAccepted(false);
    setToast(`Đã bỏ ${getItem(line.itemId)?.name ?? "món"} khỏi giỏ.`);
  };

  const undoRemove = () => {
    if (!lastRemoved) return;
    setCart((current) => [...current, lastRemoved]);
    setLastRemoved(null);
    setToast("Đã hoàn tác.");
  };

  const confirmOrder = () => {
    setCheckoutError("");
    setPaymentError("");
    if (!cart.length) {
      setCheckoutError("Giỏ đang trống.");
      return;
    }
    if (mode === "delivery" && (!addressId || deliveryFee < 0)) {
      setCheckoutError("Chọn một địa chỉ trong vùng giao trước khi tiếp tục.");
      return;
    }
    if (allergens.length && !allergenAccepted) {
      setCheckoutError("Bạn cần xác nhận đã đọc cảnh báo dị ứng.");
      return;
    }

    if (order && additionalRound && mode === "table") {
      setOrder({
        ...order,
        cart: [...order.cart, ...cart],
        subtotal: order.subtotal + subtotal,
        total: order.total + subtotal,
        statusIndex: 0,
        rounds: order.rounds + 1
      });
    } else {
      setOrder({
        id: "MB-7312",
        mode: mode!,
        cart: [...cart],
        subtotal,
        deliveryFee: Math.max(0, deliveryFee),
        total,
        addressId: mode === "delivery" ? addressId : undefined,
        tableId: mode === "table" ? "Bàn 12" : undefined,
        statusIndex: 0,
        rounds: 1
      });
    }
    setAdditionalRound(false);
    setCart([]);
    setAllergenAccepted(false);
    setTab("order");
    setToast("Bếp đã nhận đơn mô phỏng.");
  };

  const tryPaymentFailure = () => {
    setPaymentError("ZaloPay mô phỏng chưa hoàn tất. Không có tiền thật bị trừ.");
  };

  const advanceOrder = () => {
    if (!order) return;
    const labels = statusLabels[order.mode];
    setOrder({ ...order, statusIndex: Math.min(order.statusIndex + 1, labels.length - 1) });
  };

  const addRound = () => {
    if (!order || order.mode !== "table") return;
    setAdditionalRound(true);
    setTab("menu");
    setToast("Lượt gọi thêm sẽ được gắn vào Bàn 12.");
  };

  const requestModeChange = () => {
    setMenuOpen(false);
    if (cart.length) {
      setSwitchConfirm(true);
      return;
    }
    performModeChange();
  };

  const performModeChange = () => {
    setMode(null);
    setChoosingAddress(false);
    setAddressId("");
    setCart([]);
    setOrder(null);
    setAdditionalRound(false);
    setAllergenAccepted(false);
    setSwitchConfirm(false);
    setTab("menu");
  };

  const resetDemo = () => {
    performModeChange();
    setCategory(categories[0]);
    setOpenItemId(null);
    setModifiers({});
    setItemNote("");
    setAddressError("");
    setCheckoutError("");
    setPaymentError("");
    setMenuOpen(false);
    setLastRemoved(null);
    setNextLineNumber(1);
    setToast("Đã đặt lại quán demo.");
  };

  const renderEntry = () => (
    <section className="screen entry-screen" aria-labelledby="entry-title">
      <div className="entry-bowl" aria-hidden="true">
        <span className="steam steam-one" />
        <span className="steam steam-two" />
        <BowlFood weight="duotone" />
      </div>
      <p className="eyebrow">MÌ BẢY · QUẬN 3</p>
      <h1 id="entry-title">{choosingAddress ? "Giao tới đâu?" : "Bạn đang ăn ở đâu?"}</h1>
      <p className="lead">
        {choosingAddress
          ? "Chọn một địa chỉ fixture để xem phí và thời gian giao."
          : "Chọn đúng ngữ cảnh để bếp gắn đơn vào bàn hoặc địa chỉ giao."}
      </p>
      {choosingAddress ? (
        <>
          <div className="address-list">
            {addresses.map((address) => {
              const fee = getDeliveryFee(address.id);
              return (
                <button type="button" key={address.id} onClick={() => chooseAddress(address)} className={fee < 0 ? "out-of-range" : ""}>
                  <span className="address-icon"><MapPin weight="duotone" aria-hidden="true" /></span>
                  <span>
                    <strong>{address.label}</strong>
                    <small>{address.detail}</small>
                    <b>{address.distance.toLocaleString("vi-VN")} km · {fee < 0 ? "Ngoài vùng" : `phí ${formatMoney(fee)}`}</b>
                  </span>
                  <CaretRight aria-hidden="true" />
                </button>
              );
            })}
          </div>
          {addressError ? <InlineAlert>{addressError}</InlineAlert> : null}
          <button type="button" className="plain-button" onClick={() => setChoosingAddress(false)}>Quay lại chọn cách ăn</button>
        </>
      ) : (
        <div className="entry-options">
          <button type="button" className="entry-option table-option" onClick={enterTable}>
            <span className="entry-number">01</span>
            <QrCode weight="duotone" aria-hidden="true" />
            <strong>Tại bàn</strong>
            <small>Gắn đơn vào Bàn 12 bằng QR mô phỏng. Không dùng camera thật.</small>
            <span className="entry-cta">Vào Bàn 12 <CaretRight /></span>
          </button>
          <button type="button" className="entry-option delivery-option" onClick={startDelivery}>
            <span className="entry-number">02</span>
            <Motorcycle weight="duotone" aria-hidden="true" />
            <strong>Giao tận nơi</strong>
            <small>Chọn địa chỉ mẫu, xem phí và theo dõi shipper scripted.</small>
            <span className="entry-cta">Chọn địa chỉ <CaretRight /></span>
          </button>
        </div>
      )}
      <div className="simulation-note">
        <Warning weight="fill" aria-hidden="true" />
        <span>QR, vị trí, ZaloPay, ZNS, bếp và giao hàng đều là mô phỏng.</span>
      </div>
    </section>
  );

  const renderMenu = () => (
    <section className="screen menu-screen" aria-labelledby="menu-title">
      <div className="menu-intro">
        <p className="eyebrow">BẾP TỐI · 17:00–22:30</p>
        <h1 id="menu-title">Tối nay ăn tô nào?</h1>
        <p>Nước dùng nấu trong ngày. Món hết vẫn được giữ trên bảng để bạn không phải đoán.</p>
      </div>
      <div className="category-rail" role="tablist" aria-label="Nhóm thực đơn">
        {categories.map((itemCategory) => (
          <button
            key={itemCategory}
            type="button"
            role="tab"
            aria-selected={category === itemCategory}
            className={category === itemCategory ? "active" : ""}
            onClick={() => setCategory(itemCategory)}
          >
            {itemCategory}
          </button>
        ))}
      </div>
      <div className="menu-board">
        {visibleItems.map((item, index) => (
          <article className={`menu-item ${item.available ? "" : "sold-out"}`} key={item.id}>
            <button
              type="button"
              className="menu-item-hit"
              disabled={!item.available}
              onClick={() => openItemSheet(item)}
              aria-label={item.available ? `Chọn ${item.name}` : `${item.name} đã hết hôm nay`}
            >
              <div className={`item-media ${item.image ? "" : "graphic"}`}>
                {item.image ? (
                  <img src={`${import.meta.env.BASE_URL}${item.image}`} alt="" />
                ) : (
                  <>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    {item.category === "Món thêm" ? <ForkKnife weight="duotone" /> : item.category === "Nước" ? <Basket weight="duotone" /> : <BowlFood weight="duotone" />}
                  </>
                )}
                {item.stamp ? <b className="item-stamp">{item.stamp}</b> : null}
              </div>
              <div className="item-copy">
                <div className="item-topline">
                  <span>{item.category}</span>
                  {item.allergens.length ? <span className="allergen-mini">{item.allergens.join(" · ")}</span> : null}
                </div>
                <h2>{item.name}</h2>
                <p>{item.description}</p>
                <div className="item-price-row">
                  <strong>{formatMoney(item.price)}</strong>
                  <span className="add-square" aria-hidden="true">{item.available ? <Plus weight="bold" /> : <X weight="bold" />}</span>
                </div>
              </div>
            </button>
          </article>
        ))}
      </div>
      {cartCount ? (
        <button type="button" className="floating-cart" onClick={() => setTab("cart")}>
          <span>{cartCount}</span>
          <strong>Xem giỏ</strong>
          <b>{formatMoney(subtotal)}</b>
          <CaretRight weight="bold" />
        </button>
      ) : null}
    </section>
  );

  const renderCart = () => (
    <section className="screen cart-screen" aria-labelledby="cart-title">
      <div className="section-heading">
        <p className="eyebrow">{mode === "table" ? "BÀN 12" : "GIAO TẬN NƠI"}</p>
        <h1 id="cart-title">Kiểm tra đơn</h1>
        <p>{mode === "table" ? "Món sẽ được gắn vào cùng ticket Bàn 12." : "Phí giao được tính trước khi xác nhận."}</p>
      </div>
      {cart.length ? (
        <>
          <div className="cart-lines">
            {cart.map((line) => {
              const item = getItem(line.itemId)!;
              const modifierLabels = Object.entries(line.modifiers)
                .map(([groupId, optionId]) => item.modifierGroups.find((group) => group.id === groupId)?.options.find((option) => option.id === optionId)?.label)
                .filter(Boolean);
              return (
                <article className="cart-line" key={line.id}>
                  <div className="cart-line-main">
                    <span className="cart-index">{String(cart.indexOf(line) + 1).padStart(2, "0")}</span>
                    <div>
                      <h2>{item.name}</h2>
                      <p>{modifierLabels.join(" · ") || "Không có tuỳ chọn"}</p>
                      {line.note ? <small>“{line.note}”</small> : null}
                    </div>
                    <strong>{formatMoney(getLineUnitPrice(line) * line.quantity)}</strong>
                  </div>
                  <div className="cart-line-actions">
                    <div className="quantity-control" aria-label={`Số lượng ${item.name}`}>
                      <button type="button" onClick={() => updateQuantity(line.id, -1)} aria-label={`Giảm ${item.name}`}><Minus /></button>
                      <span>{line.quantity}</span>
                      <button type="button" onClick={() => updateQuantity(line.id, 1)} aria-label={`Tăng ${item.name}`}><Plus /></button>
                    </div>
                    <button type="button" className="remove-button" onClick={() => removeLine(line)} aria-label={`Bỏ ${item.name}`}><Trash /></button>
                  </div>
                </article>
              );
            })}
          </div>

          {lastRemoved ? (
            <button type="button" className="undo-row" onClick={undoRemove}>Hoàn tác món vừa bỏ</button>
          ) : null}

          {mode === "delivery" && selectedAddress ? (
            <div className="delivery-address">
              <MapPin weight="fill" aria-hidden="true" />
              <span>
                <strong>Giao tới · {selectedAddress.label}</strong>
                <small>{selectedAddress.detail}</small>
                <b>{selectedAddress.distance.toLocaleString("vi-VN")} km · phí {formatMoney(deliveryFee)}</b>
              </span>
              <button type="button" onClick={startDelivery}>Đổi</button>
            </div>
          ) : null}

          {allergens.length ? (
            <label className="allergen-check">
              <input type="checkbox" checked={allergenAccepted} onChange={(event) => {
                setAllergenAccepted(event.target.checked);
                setCheckoutError("");
              }} />
              <span>
                <strong>Cảnh báo: có {allergens.join(" và ").toLowerCase()}</strong>
                <small>Tôi đã đọc cảnh báo; bếp demo không thể bảo đảm không nhiễm chéo.</small>
              </span>
            </label>
          ) : null}

          <div className="receipt-summary">
            <div><span>Tạm tính</span><strong>{formatMoney(subtotal)}</strong></div>
            {mode === "delivery" ? <div><span>Phí giao</span><strong>{formatMoney(Math.max(0, deliveryFee))}</strong></div> : null}
            <div className="receipt-total"><span>Tổng</span><strong>{formatMoney(total)}</strong></div>
          </div>

          <fieldset className="payment-methods">
            <legend>Thanh toán mô phỏng</legend>
            <label className={paymentMethod === "zalopay" ? "selected" : ""}>
              <input type="radio" name="payment" value="zalopay" checked={paymentMethod === "zalopay"} onChange={() => setPaymentMethod("zalopay")} />
              <CreditCard weight="duotone" />
              <span><strong>ZaloPay</strong><small>Không mở ví hoặc trừ tiền thật</small></span>
            </label>
            <label className={paymentMethod === "cod" ? "selected" : ""}>
              <input type="radio" name="payment" value="cod" checked={paymentMethod === "cod"} onChange={() => setPaymentMethod("cod")} />
              <Receipt weight="duotone" />
              <span><strong>{mode === "table" ? "Thanh toán tại quầy" : "Thanh toán khi nhận"}</strong><small>Chỉ ghi vào đơn demo</small></span>
            </label>
          </fieldset>
          {checkoutError ? <InlineAlert>{checkoutError}</InlineAlert> : null}
          {paymentError ? <InlineAlert>{paymentError}</InlineAlert> : null}
          <button type="button" className="plain-button payment-fail" onClick={tryPaymentFailure}>Xem kịch bản lỗi thanh toán</button>
          <button type="button" className="checkout-button" onClick={confirmOrder}>
            Xác nhận {paymentMethod === "zalopay" ? "ZaloPay mô phỏng" : "đơn"} · {formatMoney(total)}
            <CaretRight weight="bold" />
          </button>
        </>
      ) : (
        <div className="empty-state">
          <ShoppingCart weight="duotone" aria-hidden="true" />
          <h2>Giỏ đang trống.</h2>
          <p>Chọn một tô nóng trước nhé.</p>
          <button type="button" onClick={() => setTab("menu")}>Xem thực đơn</button>
        </div>
      )}
    </section>
  );

  const renderOrder = () => {
    if (!order) {
      return (
        <section className="screen order-screen" aria-labelledby="order-empty-title">
          <div className="empty-state">
            <Receipt weight="duotone" aria-hidden="true" />
            <h1 id="order-empty-title">Chưa có đơn.</h1>
            <p>Khi xác nhận, ticket bếp sẽ chạy ở đây.</p>
            <button type="button" onClick={() => setTab("menu")}>Xem thực đơn</button>
          </div>
        </section>
      );
    }
    const labels = statusLabels[order.mode];
    const isComplete = order.statusIndex === labels.length - 1;
    return (
      <section className="screen order-screen" aria-labelledby="order-title">
        <div className="order-kicker">
          <span>{order.mode === "table" ? order.tableId : "GIAO TẬN NƠI"}</span>
          <span>LƯỢT {order.rounds}</span>
        </div>
        <div className="order-ticket">
          <div className="ticket-edge" aria-hidden="true" />
          <p className="eyebrow">BẾP ĐÃ NHẬN</p>
          <h1 id="order-title">Đơn #{order.id}</h1>
          <p>{order.mode === "table" ? "Dự kiến lên món trong 18–24 phút." : "Dự kiến giao trong 35–42 phút."}</p>
          <div className="order-total"><span>{order.cart.reduce((sum, line) => sum + line.quantity, 0)} món</span><strong>{formatMoney(order.total)}</strong></div>
        </div>
        <ol className="status-timeline" aria-label="Trạng thái đơn">
          {labels.map((label, index) => (
            <li key={label} className={index < order.statusIndex ? "done" : index === order.statusIndex ? "current" : ""}>
              <span className="status-dot">{index <= order.statusIndex ? <Check weight="bold" /> : null}</span>
              <div>
                <strong>{label}</strong>
                <small>{index === order.statusIndex ? "Trạng thái hiện tại · fixture" : index < order.statusIndex ? "Đã xong" : "Đang chờ"}</small>
              </div>
            </li>
          ))}
        </ol>
        {!isComplete ? (
          <button type="button" className="advance-button" onClick={advanceOrder}>
            <CookingPot weight="duotone" /> Mô phỏng bước tiếp
          </button>
        ) : (
          <div className="complete-banner">
            <Check weight="bold" />
            <span><strong>{order.mode === "table" ? "Món đã lên bàn." : "Đơn đã giao."}</strong><small>Chỉ là trạng thái scripted trong trình duyệt.</small></span>
          </div>
        )}
        <div className="zns-row">
          <span>ZNS</span>
          <p>Cập nhật ZNS chỉ hiển thị trong demo, không gửi thật.</p>
        </div>
        {order.mode === "table" ? (
          <button type="button" className="add-round-button" onClick={addRound}>
            <Plus weight="bold" /> Gọi thêm cho Bàn 12
          </button>
        ) : null}
      </section>
    );
  };

  const renderAppContent = () => {
    if (!mode || choosingAddress) return renderEntry();
    if (tab === "menu") return renderMenu();
    if (tab === "cart") return renderCart();
    return renderOrder();
  };

  return (
    <main className="page-shell">
      <aside className="brand-panel" aria-label="Giới thiệu MÌ BẢY">
        <div className="brand-photo">
          <img src={`${import.meta.env.BASE_URL}media/mi-quang.webp`} alt="Tô mì Quảng gà nướng với rau và bánh tráng" />
          <div className="brand-photo-stamp">BẾP TỐI · 19:15</div>
        </div>
        <div className="brand-copy">
          <div className="big-wordmark"><span>MÌ</span><span>BẢY</span></div>
          <p>Mì Việt, gọi gọn, bếp làm rõ.</p>
          <div className="brand-ledger">
            <span><b>07</b> món tối</span>
            <span><b>02</b> cách gọi</span>
            <span><b>00</b> giao dịch thật</span>
          </div>
          <small>MÌ BẢY là thương hiệu hư cấu. Giao diện mô phỏng Zalo Mini App, QR, vị trí, thanh toán và vận hành bếp.</small>
        </div>
      </aside>

      <section className="app-frame" aria-label="MÌ BẢY Zalo Mini App mô phỏng">
        <div className="simulation-bar">
          <span>ZALO MINI APP · MÔ PHỎNG</span>
          <span>19:15</span>
        </div>
        <header className="app-header">
          <div className="app-brand">
            <span className="bowl-mark"><BowlFood weight="fill" /></span>
            <div><strong>MÌ BẢY</strong><small>QUẬN 3 · TP.HCM</small></div>
          </div>
          <button type="button" className="host-menu-button" onClick={() => setMenuOpen((open) => !open)} aria-expanded={menuOpen} aria-label="Mở menu demo">
            <DotsThree weight="bold" />
          </button>
          {menuOpen ? (
            <div className="host-menu" role="menu">
              <button type="button" role="menuitem" onClick={requestModeChange}><ArrowCounterClockwise /> Đổi cách gọi món</button>
              <button type="button" role="menuitem" onClick={resetDemo}><Trash /> Đặt lại quán demo</button>
            </div>
          ) : null}
        </header>

        {mode && !choosingAddress ? (
          <button className={`context-baton ${mode}`} type="button" onClick={requestModeChange}>
            <span>{mode === "table" ? <QrCode weight="bold" /> : <Truck weight="bold" />}</span>
            <strong>{mode === "table" ? "BÀN 12 · QR MÔ PHỎNG" : `${selectedAddress?.label.toUpperCase()} · ${selectedAddress?.distance.toLocaleString("vi-VN")} KM`}</strong>
            <small>đổi</small>
          </button>
        ) : null}

        <div className="app-content">{renderAppContent()}</div>

        {mode && !choosingAddress ? (
          <nav className="bottom-nav" aria-label="Điều hướng chính">
            <BottomNavButton active={tab === "menu"} label="Thực đơn" icon={<BowlFood />} onClick={() => setTab("menu")} />
            <BottomNavButton active={tab === "cart"} label={`Giỏ${cartCount ? ` · ${cartCount}` : ""}`} icon={<ShoppingCart />} onClick={() => setTab("cart")} />
            <BottomNavButton active={tab === "order"} label="Đơn" icon={<Receipt />} onClick={() => setTab("order")} />
          </nav>
        ) : null}
      </section>

      {openItem ? (
        <div className="sheet-backdrop" role="presentation">
          <section className="item-sheet" role="dialog" aria-modal="true" aria-labelledby="item-title">
            <button type="button" className="sheet-close" onClick={() => setOpenItemId(null)} aria-label="Đóng"><X /></button>
            <div className="sheet-item-head">
              {openItem.image ? <img src={`${import.meta.env.BASE_URL}${openItem.image}`} alt="" /> : <div className="sheet-graphic"><BowlFood weight="duotone" /></div>}
              <div>
                <p className="eyebrow">{openItem.category}</p>
                <h2 id="item-title">{openItem.name}</h2>
                <p>{openItem.description}</p>
                <strong>{formatMoney(openItem.price)}</strong>
              </div>
            </div>
            {openItem.allergens.length ? (
              <div className="allergen-sheet-alert"><Warning weight="fill" /><span>Có {openItem.allergens.join(" và ").toLowerCase()}. Xác nhận cảnh báo ở giỏ hàng.</span></div>
            ) : null}
            <div className="modifier-groups">
              {openItem.modifierGroups.map((group) => (
                <fieldset key={group.id}>
                  <legend>{group.label} {group.required ? <span>Bắt buộc</span> : <span>Tuỳ chọn</span>}</legend>
                  <div className="modifier-options">
                    {group.options.map((option) => (
                      <label key={option.id} className={modifiers[group.id] === option.id ? "selected" : ""}>
                        <input
                          type="radio"
                          name={`modifier-${group.id}`}
                          value={option.id}
                          checked={modifiers[group.id] === option.id}
                          onChange={() => {
                            setModifiers((current) => ({ ...current, [group.id]: option.id }));
                            setItemError("");
                          }}
                        />
                        <span>{option.label}</span>
                        <b>{option.price ? `+${formatMoney(option.price)}` : "0đ"}</b>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
            <label className="note-label" htmlFor="item-note">Ghi chú cho bếp</label>
            <textarea id="item-note" value={itemNote} maxLength={120} onChange={(event) => setItemNote(event.target.value)} placeholder="Ví dụ: để rau riêng, không hành…" />
            {itemError ? <InlineAlert>{itemError}</InlineAlert> : null}
            <button type="button" className="sheet-add-button" onClick={addItem}>
              Thêm vào giỏ · {formatMoney(openItem.price)}
              <Plus weight="bold" />
            </button>
          </section>
        </div>
      ) : null}

      {switchConfirm ? (
        <div className="dialog-backdrop" role="presentation">
          <section className="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="switch-title">
            <button type="button" className="sheet-close" onClick={() => setSwitchConfirm(false)} aria-label="Đóng"><X /></button>
            <Warning className="confirm-icon" weight="duotone" />
            <p className="eyebrow">ĐỔI CÁCH GỌI MÓN</p>
            <h2 id="switch-title">Xoá giỏ hiện tại?</h2>
            <p>Giỏ đang có {cartCount} món. Đổi giữa Bàn 12 và giao tận nơi sẽ xoá giỏ để tránh gửi sai context.</p>
            <button type="button" className="danger-button" onClick={performModeChange}>Xoá giỏ và đổi</button>
            <button type="button" className="secondary-button" onClick={() => setSwitchConfirm(false)}>Giữ cách hiện tại</button>
          </section>
        </div>
      ) : null}

      <div className={`toast ${toast ? "visible" : ""}`} role="status" aria-live="polite">
        <Check weight="bold" /> <span>{toast}</span>
        {lastRemoved && toast.startsWith("Đã bỏ") ? <button type="button" onClick={undoRemove}>Hoàn tác</button> : null}
      </div>
    </main>
  );
}

function BottomNavButton({ active, label, icon, onClick }: { active: boolean; label: string; icon: React.ReactNode; onClick: () => void }) {
  return (
    <button type="button" className={active ? "active" : ""} aria-current={active ? "page" : undefined} onClick={onClick}>
      {icon}<span>{label}</span>
    </button>
  );
}

function InlineAlert({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-alert" role="alert">
      <Warning weight="fill" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}
